import { resolveValueOrArray, ValueOrArray } from './resolveValueOrArray'
import { KeysOfType } from './types'

/**
 * reduces the supplied values into a {@link Map} of {@link key} to {@link T|value}
 */
export function reduceToMap<T>(
  values: ValueOrArray<T>,
  key: (KeysOfType<T, string> & string) | ((value: T) => string)
): Map<string, T>
/**
 * reduces the supplied values into a {@link Map} of {@link key} to a new
 * {@link transform|transformed} value
 */
export function reduceToMap<T, U>(
  values: ValueOrArray<T>,
  key: (KeysOfType<T, string> & string) | ((value: T) => string),
  transform: (value: T) => U
): Map<string, U>
//
export function reduceToMap<T, U = T>(
  values: ValueOrArray<T>,
  key: (KeysOfType<T, string> & string) | ((value: T) => string),
  transform?: (value: T) => U
) {
  return resolveValueOrArray(values).reduce(
    (map, value) => map.set(getKey(key, value), transform?.(value) ?? value),
    new Map<string, T | U>()
  )
}

/**
 * reduces the supplied values into a {@link Map} of {@link key} to {@link T|value}
 * for async values
 */
export function reduceToMapAsync<T>(
  values: Promise<ValueOrArray<T>>,
  keyFn: (KeysOfType<T, string> & string) | ((value: T) => string)
): Promise<Map<string, T>>
/**
 * reduces the supplied values into a {@link Map} of {@link key} to a new
 * {@link transform|transformed} value for async values
 */
export function reduceToMapAsync<T, U>(
  values: Promise<ValueOrArray<T>>,
  keyFn: (KeysOfType<T, string> & string) | ((value: T) => string),
  transform: (value: T) => U
): Promise<Map<string, U>>
//
export async function reduceToMapAsync<T, U = T>(
  values: Promise<ValueOrArray<T>>,
  keyFn: (KeysOfType<T, string> & string) | ((value: T) => string),
  transform?: (value: T) => U
) {
  const awaited = await values
  if (transform) return reduceToMap(awaited, keyFn, transform)
  return reduceToMap(awaited, keyFn)
}

/**
 * reduces the supplied values into a {@link Map} of {@link key} to an array of
 * {@link T|values}
 */
export function reduceToGroups<T>(
  values: ValueOrArray<T>,
  key: (KeysOfType<T, string> & string) | ((value: T) => string)
): Map<string, T[]>
/**
 * reduces the supplied values into a {@link Map} of {@link key} to an array of
 * alternate {@link U|values} pulled from each {@link T|value}
 */
export function reduceToGroups<T, U>(
  values: ValueOrArray<T>,
  key: (KeysOfType<T, string> & string) | ((value: T) => string),
  transform: (value: T) => U
): Map<string, U[]>
//
export function reduceToGroups<T, U = T>(
  values: ValueOrArray<T>,
  key: (KeysOfType<T, string> & string) | ((value: T) => string),
  transform?: (value: T) => U
) {
  return resolveValueOrArray(values).reduce((map, value) => {
    const got = map.get(getKey(key, value)) ?? []
    got.push(transform?.(value) ?? value)
    return map.set(getKey(key, value), got)
  }, new Map<string, (T | U)[]>())
}

/**
 * reduces the supplied values into a {@link Map} of {@link key} to an array of
 * {@link T|values} for async values
 */
export function reduceToGroupsAsync<T>(
  values: Promise<ValueOrArray<T>>,
  key: (KeysOfType<T, string> & string) | ((value: T) => string)
): Promise<Map<string, T[]>>
/**
 * reduces the supplied values into a {@link Map} of {@link key} to an array of
 * alternate {@link U|values} pulled from each {@link T|value} for async values
 */
export function reduceToGroupsAsync<T, U>(
  values: Promise<ValueOrArray<T>>,
  key: (KeysOfType<T, string> & string) | ((value: T) => string),
  transform: (value: T) => U
): Promise<Map<string, U[]>>
//
export async function reduceToGroupsAsync<T, U>(
  values: Promise<ValueOrArray<T>>,
  key: (KeysOfType<T, string> & string) | ((value: T) => string),
  transform?: (value: T) => U
) {
  const awaited = await values
  if (transform) return reduceToGroups(awaited, key, transform)
  return reduceToGroups(awaited, key)
}

function getKey<T>(
  key: (KeysOfType<T, string> & string) | ((value: T) => string),
  value: T
) {
  return typeof key === 'function' ? key(value) : String(value[key])
}
